import { useState } from 'react'
import { Folder } from 'lucide-react'
import { Button, Modal } from '@/components/ui'
import { cn } from '@/lib/cn'
import { useDriveFolders } from '../../hooks/useDrive'
import { countLabel, flattenFolders } from '../../utils/driveTree'

export interface DriveMoveDialogProps {
  companyId: string
  fileCount: number
  /** Where the files are now, so it isn't offered as the destination. */
  currentFolderId?: string
  isPending: boolean
  onClose: () => void
  onMove: (folderId: string) => void
}

/** Choose a folder in the same company. Mount it only while it is open. */
export function DriveMoveDialog({
  companyId,
  fileCount,
  currentFolderId,
  isPending,
  onClose,
  onMove,
}: DriveMoveDialogProps): React.ReactElement {
  const { data } = useDriveFolders(companyId)
  const [targetId, setTargetId] = useState<string | null>(null)
  const options = flattenFolders(data?.folders ?? [])

  return (
    <Modal open onClose={onClose} title={`Move ${countLabel(fileCount, 'file')}`}>
      <div className="space-y-4">
        <ul
          role="radiogroup"
          aria-label="Destination folder"
          className="max-h-[50vh] space-y-0.5 overflow-y-auto"
        >
          {options.map(({ folder, depth }) => {
            const isCurrent = folder.id === currentFolderId
            const isTarget = folder.id === targetId
            return (
              <li key={folder.id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isTarget}
                  disabled={isCurrent}
                  onClick={() => setTargetId(folder.id)}
                  style={{ paddingLeft: 8 + depth * 16 }}
                  className={cn(
                    'flex h-9 w-full items-center gap-2 rounded-md pr-2 text-left text-sm transition-colors',
                    isTarget
                      ? 'bg-primary/10 font-medium text-primary'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/60',
                    isCurrent && 'cursor-default opacity-50 hover:bg-transparent',
                  )}
                >
                  <Folder className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="truncate">{folder.name}</span>
                  {isCurrent ? <span className="ml-auto shrink-0 text-xs">Here now</span> : null}
                </button>
              </li>
            )
          })}
        </ul>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={isPending}
            disabled={!targetId}
            onClick={() => {
              if (targetId) onMove(targetId)
            }}
            className="whitespace-nowrap"
          >
            Move here
          </Button>
        </div>
      </div>
    </Modal>
  )
}

DriveMoveDialog.displayName = 'DriveMoveDialog'
